<template>
  <figure class="arc" :class="{ 'is-big': big }" ref="wrap">
    <figcaption class="arc-head">
      <span class="arc-title">{{ chart.title }}</span>
      <span class="arc-sub" v-if="subtitle">{{ subtitle }}</span>
    </figcaption>

    <p v-if="empty" class="arc-empty">No figures for these dates.</p>
    <!-- totals only: one figure a line -->
    <dl v-else-if="tableView && list" class="arc-list">
      <template v-for="f in list" :key="f.label"><dt>{{ f.label }}</dt><dd>{{ f.text }}</dd></template>
    </dl>
    <div v-else-if="tableView" class="arc-table" :class="{ 'is-capped': !big }">
      <table>
        <thead>
          <tr><th v-for="(c, i) in table.columns" :key="i" :class="{ 'is-num': c.unit !== 'text' }" scope="col">{{ c.label }}</th></tr>
        </thead>
        <tbody>
          <tr v-for="(r, ri) in table.rows" :key="ri">
            <td v-for="(v, i) in r" :key="i" :class="{ 'is-num': table.columns[i].unit !== 'text' }">{{ cell(v, table.columns[i]) }}</td>
          </tr>
        </tbody>
        <tfoot v-if="table.total">
          <tr><td v-for="(v, i) in table.total" :key="i" :class="{ 'is-num': table.columns[i].unit !== 'text' }">{{ i === 0 ? "Total" : v === null ? "" : cell(v, table.columns[i]) }}</td></tr>
        </tfoot>
      </table>
    </div>
    <!-- The picture is built from the chart's figures with every text escaped (utils/assistantCharts). -->
    <div v-else class="arc-svg" v-html="svg"></div>

    <div class="arc-foot">
      <p class="arc-note" v-if="compare">{{ compare }}</p>
      <p class="arc-note" v-if="chart.chart_note && !tableView">{{ chart.chart_note }}</p>
      <p class="arc-note" v-if="chart.note">{{ chart.note }}</p>
      <div class="arc-actions">
        <button type="button" class="arc-btn" v-if="hidden && !tableView" @click="all = !all">{{ all ? "Show fewer" : "Show all " + chart.labels.length }}</button>
        <button type="button" class="arc-btn" v-if="canToggle" :aria-pressed="showTable ? 'true' : 'false'" @click="showTable = !showTable">{{ showTable ? "Chart" : "Table" }}</button>
        <button type="button" class="arc-btn" v-if="!big && !empty" ref="expand" @click="open = true">Expand</button>
        <button type="button" class="arc-btn" v-if="big" @click="$emit('close')">Close</button>
      </div>
    </div>

    <!-- Expand: the same chart, wide, over the page. -->
    <Teleport to="body" v-if="open">
      <div class="arc-modal" role="dialog" aria-modal="true" :aria-label="chart.title" @click.self="open = false" @keydown.esc="open = false" tabindex="-1" ref="modal">
        <div class="arc-modal-box">
          <AssistantChart :chart="chart" big @close="open = false" />
        </div>
      </div>
    </Teleport>
  </figure>
</template>

<script>
import { chartSvg, chartSubtitle, compareText, cellText, figureList, hasData, hiddenRows } from "@/utils/assistantCharts";

const ROWS = 12;   // bars across shown before "Show all"

/**
 * One chart or table that came with Connect's reply (the rich part of a message, 2026-10-20). The picture is
 * drawn at the width it gets (measured, as AreaChart does), so it stays sharp in the narrow drawer and in
 * the wide view; "Table" shows every figure behind it; "Expand" opens it wide over the page.
 */
export default {
  name: "AssistantChart",
  props: {
    chart: { type: Object, required: true },
    big: { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return { w: 320, all: false, showTable: false, open: false, ro: null };
  },
  computed: {
    table() { return this.chart.table || { columns: [], rows: [], total: null }; },
    list() { return figureList(this.table); },
    isTable() { return this.chart.type === "table"; },
    drawable() { return hasData(this.chart); },
    empty() { return this.isTable ? !this.table.rows.length : !this.drawable; },
    tableView() { return this.isTable || this.showTable; },
    canToggle() { return !this.isTable && this.drawable && this.table.rows.length > 0; },
    hidden() { return this.big ? 0 : hiddenRows(this.chart, ROWS); },
    subtitle() { return chartSubtitle(this.chart); },
    compare() { return compareText(this.chart); },
    svg() {
      if (this.tableView || this.empty) return "";
      return chartSvg(this.chart, this.w, { maxRows: this.big || this.all ? Infinity : ROWS, height: this.big ? 320 : 200 });
    },
  },
  watch: {
    // The wide view takes the keyboard while it is open (Esc closes it) and gives it back to Expand.
    open(now) {
      this.$nextTick(() => { const el = now ? this.$refs.modal : this.$refs.expand; if (el) el.focus(); });
    },
  },
  mounted() {
    this.measure();
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.wrap);
    }
  },
  beforeUnmount() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    measure() {
      const el = this.$refs.wrap;
      // the figure's padding on both sides is not drawing room
      if (el && el.clientWidth) this.w = Math.max(200, el.clientWidth - 28);
    },
    cell: cellText,
  },
};
</script>

<style scoped>
.arc { box-sizing: border-box; width: 100%; margin: 0; padding: 12px 14px 10px; border: 1px solid var(--border-primary); border-radius: 14px; background: var(--bg-primary); box-shadow: var(--shadow-sm); }
.arc.is-big { border: 0; box-shadow: none; padding: 4px 6px; }
.arc-head { display: flex; flex-direction: column; gap: 1px; margin-bottom: 10px; }
.arc-title { font-size: var(--t-body-sm); font-weight: 650; line-height: 1.4; color: var(--text-primary); }
.arc.is-big .arc-title { font-size: 16px; }
.arc-sub { font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.arc-empty { margin: 6px 0; font-size: var(--t-body-sm); color: var(--text-muted); }
.arc-svg { width: 100%; overflow: hidden; }
.arc-svg :deep(svg) { display: block; max-width: 100%; height: auto; }
.arc-table { box-sizing: border-box; width: 100%; overflow: auto; border: 1px solid var(--border-light); border-radius: 10px; }
.arc-table.is-capped { max-height: 320px; }
.arc-table table { width: 100%; border-collapse: collapse; font-size: var(--t-caption); line-height: 1.4; }
.arc-table th { position: sticky; top: 0; z-index: 1; padding: 6px 8px; background: var(--bg-secondary); color: var(--text-muted); font-weight: 600; text-align: left; white-space: nowrap; border-bottom: 1px solid var(--border-primary); }
.arc-table td { padding: 5px 8px; color: var(--text-primary); border-bottom: 1px solid var(--border-light); white-space: nowrap; }
.arc-table td:first-child { white-space: normal; min-width: 7rem; }
.arc-table .is-num { text-align: right; font-variant-numeric: tabular-nums; }
.arc-table tfoot td { font-weight: 650; border-top: 1px solid var(--border-secondary); border-bottom: 0; background: var(--bg-secondary); }
.arc-list { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0; margin: 0; font-size: var(--t-body-sm); line-height: 1.45; }
.arc-list dt, .arc-list dd { margin: 0; padding: 5px 0; border-bottom: 1px solid var(--border-light); }
.arc-list dt { color: var(--text-secondary); padding-right: 12px; }
.arc-list dd { color: var(--text-primary); font-weight: 650; text-align: right; font-variant-numeric: tabular-nums; }
.arc-foot { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
.arc-note { margin: 0; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.arc-actions { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
.arc-btn { min-height: 26px; padding: 2px 11px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-caption); font-weight: 600; cursor: pointer; }
.arc-btn:hover { border-color: var(--brand-orange); color: var(--text-primary); }
.arc-btn[aria-pressed="true"] { border-color: var(--accent-primary); background: var(--accent-primary-bg); color: var(--text-link); }
.arc-modal { position: fixed; inset: 0; z-index: var(--z-modal); display: flex; align-items: center; justify-content: center; padding: 24px 16px; background: var(--bg-overlay); outline: none; }
.arc-modal-box { box-sizing: border-box; width: min(980px, 100%); max-height: 100%; overflow: auto; padding: 18px 20px; border-radius: var(--card-radius, 16px); background: var(--bg-modal); box-shadow: var(--shadow-lg); }
</style>

<style>
/* The chart's marks are drawn as a string (utils/assistantCharts), so they take their colours from these
   classes, not from scoped styles. Light first; the dark theme lifts every colour for the dark surface. */
.arc { --arc-s0: #f97316; --arc-s1: #2563eb; --arc-s2: #059669; --arc-s3: #9333ea; --arc-s4: #0891b2; --arc-s5: #be185d; --arc-other: #94a3b8; }
[data-theme="dark"] .arc { --arc-s0: #fb923c; --arc-s1: #60a5fa; --arc-s2: #34d399; --arc-s3: #c084fc; --arc-s4: #22d3ee; --arc-s5: #f472b6; --arc-other: #64748b; }
.arc-svg text { font-family: var(--font-main); }
.arc-svg .arc-f-s0 { fill: var(--arc-s0); } .arc-svg .arc-k-s0 { stroke: var(--arc-s0); }
.arc-svg .arc-f-s1 { fill: var(--arc-s1); } .arc-svg .arc-k-s1 { stroke: var(--arc-s1); }
.arc-svg .arc-f-s2 { fill: var(--arc-s2); } .arc-svg .arc-k-s2 { stroke: var(--arc-s2); }
.arc-svg .arc-f-s3 { fill: var(--arc-s3); } .arc-svg .arc-k-s3 { stroke: var(--arc-s3); }
.arc-svg .arc-f-s4 { fill: var(--arc-s4); } .arc-svg .arc-k-s4 { stroke: var(--arc-s4); }
.arc-svg .arc-f-s5 { fill: var(--arc-s5); } .arc-svg .arc-k-s5 { stroke: var(--arc-s5); }
.arc-svg .arc-f-other { fill: var(--arc-other); } .arc-svg .arc-k-other { stroke: var(--arc-other); }
.arc-svg .arc-f-text { fill: var(--text-primary); }
.arc-svg .arc-f-axis { fill: var(--text-muted); }
.arc-svg .arc-k-grid { stroke: var(--border-primary); }
.arc-svg .arc-k-zero { stroke: var(--border-secondary); }
.arc-svg .arc-f-bg { fill: var(--bg-primary); } .arc-svg .arc-k-bg { stroke: var(--bg-primary); }
</style>
